import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import styles from './AdminPage.module.scss';
import { authFetch, openProtectedPdf } from '../auth/authFetch';

interface DocumentInfo {
  name: string;
  size: number;
  lastModified: string;
}

function AdminPage() {
  const { instance, accounts } = useMsal();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorizedUsers, setAuthorizedUsers] = useState<string[]>([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const fetchDocuments = async () => {
    try {
      const response = await authFetch('/api/admin/documents');

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Asks the server whether the signed-in user is an admin. The server decides,
   * based on validated token claims — the browser never evaluates the allow-list.
   * This gate is a UX affordance only; the API enforces the same rule independently.
   */
  const checkAdminStatus = async () => {
    try {
      const response = await authFetch('/api/admin/me');

      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const fetchAuthorizedUsers = async () => {
    try {
      const response = await authFetch('/api/admin/authorized-users');

      if (response.ok) {
        const data = await response.json();
        setAuthorizedUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching authorized users:', err);
    }
  };

  const addAuthorizedUser = async () => {
    if (!newUserEmail.trim()) {
      setUserMessage('Please enter an email address');
      return;
    }

    try {
      const response = await authFetch('/api/admin/add-authorized-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newUserEmail.trim() })
      });

      if (response.ok) {
        setUserMessage('User added successfully!');
        setNewUserEmail('');
        fetchAuthorizedUsers();
      } else {
        const error = await response.text();
        setUserMessage(`Failed to add user: ${error}`);
      }
    } catch (error) {
      setUserMessage(`Failed to add user: ${error}`);
    }
  };

  const viewPdf = async (fileName: string) => {
    try {
      await openProtectedPdf(fileName);
    } catch (error) {
      setMessage(`Could not open ${fileName}: ${error}`);
    }
  };

  useEffect(() => {
    checkAdminStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  useEffect(() => {
    if (isAdmin) {
      fetchDocuments();
      fetchAuthorizedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (checkingAuth) {
    return (
      <div className={styles.container}>
        <section className={`intro ${styles.content}`}>
          <div className="wrapper"><p>Checking authorization...</p></div>
        </section>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <section className={`intro ${styles.content}`}>
          <div className="wrapper">
            <h1 className={styles.title}>Access Denied</h1>
            <p className={styles.subtitle}>You are not authorized to access this page.</p>
            <button onClick={() => window.location.href = '/microsite'} style={{padding: '10px 20px', marginTop: '20px'}}>
              Return to Microsite
            </button>
          </div>
        </section>
      </div>
    );
  }

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setMessage('Please select a PDF file');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);

    try {
      // Content-Type is intentionally not set — the browser supplies the
      // multipart boundary, and authFetch only adds the Authorization header.
      const response = await authFetch('/api/admin/upload-document', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setMessage(`${docType} uploaded successfully!`);
        event.target.value = '';
        fetchDocuments();
      } else {
        const error = await response.text();
        setMessage(`Upload failed: ${error}`);
      }
    } catch (error) {
      setMessage(`Upload failed: ${error}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className="header">
        <div className="wrapper">
          <div className="logo">
            <img src="/images/2025/1095 - Circle of Excellence_Colored_White_300x72.png" alt="Circle of Excellence" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Logged in as: {accounts[0]?.username || accounts[0]?.name || 'User'}
            </span>
            <button
              onClick={handleLogout}
              style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className={`intro ${styles.content}`}>
        <div className="wrapper">
          <h1 className={styles.title}>Document Administration</h1>
          <p className={styles.subtitle}>Upload Circle of Excellence documents</p>

          {message && <div className={styles.message}>{message}</div>}

          <div className="cards-container">
            <div className="card">
              <h3>Qualifications Document</h3>
              <p>Upload the Circle of Excellence qualifications and rules PDF</p>
              {documents.find(d => d.name === 'Qualifications.pdf') ? (
                <div style={{ fontSize: '12px', margin: '5px 0' }}>
                  <p style={{ color: '#666', margin: '2px 0' }}>
                    Current: {new Date(documents.find(d => d.name === 'Qualifications.pdf')!.lastModified).toLocaleString()}
                  </p>
                  <button
                    onClick={() => viewPdf('Qualifications.pdf')}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#0066cc', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px' }}
                  >
                    View Qualifications.pdf
                  </button>
                </div>
              ) : (
                <p style={{ fontSize: '12px', color: '#999', margin: '5px 0' }}>No document uploaded</p>
              )}
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e, 'qualifications')}
                disabled={uploading}
                className={styles.fileInput}
              />
            </div>

            <div className="card">
              <h3>Leaderboard Document</h3>
              <p>Upload the current standings leaderboard PDF</p>
              {documents.find(d => d.name === 'Leaderboard.pdf') ? (
                <div style={{ fontSize: '12px', margin: '5px 0' }}>
                  <p style={{ color: '#666', margin: '2px 0' }}>
                    Current: {new Date(documents.find(d => d.name === 'Leaderboard.pdf')!.lastModified).toLocaleString()}
                  </p>
                  <button
                    onClick={() => viewPdf('Leaderboard.pdf')}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#0066cc', textDecoration: 'underline', cursor: 'pointer', fontSize: '12px' }}
                  >
                    View Leaderboard.pdf
                  </button>
                </div>
              ) : (
                <p style={{ fontSize: '12px', color: '#999', margin: '5px 0' }}>No document uploaded</p>
              )}
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e, 'leaderboard')}
                disabled={uploading}
                className={styles.fileInput}
              />
            </div>

            <div className="card">
              <h3>All Documents in Volume</h3>
              {loading ? (
                <p>Loading...</p>
              ) : documents.length > 0 ? (
                <div style={{ fontSize: '14px' }}>
                  {documents.map(doc => (
                    <div key={doc.name} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <button
                        onClick={() => viewPdf(doc.name)}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#13A3F7', textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}
                      >
                        {doc.name}
                      </button><br />
                      <span style={{ color: '#666' }}>
                        {(doc.size / 1024).toFixed(1)} KB - {new Date(doc.lastModified).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#999' }}>No documents found in volume</p>
              )}
            </div>

            <div className="card">
              <h3>Authorized Users</h3>
              <p>Manage users who can access the admin panel</p>

              {userMessage && (
                <div style={{ padding: '8px', margin: '8px 0', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '12px' }}>
                  {userMessage}
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <h4 style={{ fontSize: '14px', margin: '10px 0 5px 0' }}>Current Users:</h4>
                <div style={{ fontSize: '12px', maxHeight: '120px', overflowY: 'auto' }}>
                  {authorizedUsers.map((user, index) => (
                    <div key={index} style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>
                      {user}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  style={{ flex: 1, padding: '8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px' }}
                  onKeyPress={(e) => e.key === 'Enter' && addAuthorizedUser()}
                />
                <button
                  onClick={addAuthorizedUser}
                  style={{ padding: '8px 12px', fontSize: '12px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="wrapper">
          <div className="legal">&copy; {new Date().getFullYear()} Infor. All rights reserved.</div>
          <div className="brand">Designed and built by Infor Marketing</div>
        </div>
      </footer>
    </div>
  );
}

export default AdminPage;
